// This file provides utilities for Banner dimensions/layout in the current web stack.
// Kept minimal as it imports relevant values from the legacy/old-stack package.

import {
    REVAMP_HERO_DESKTOP_RATIO,
    REVAMP_HERO_MOBILE_RATIO,
} from '../modules/article-revamp/util/article-hero-box';
import {
    BANNER_DIMENSIONS,
    FULL_COLUMN_WIDTH_FULL_SCREEN,
    BANNER_HEIGHT_DESKTOP_FULL_SCREEN,
} from '../modules/page-builder/config/column-constants';

type ItemType = 'project' | 'article';

interface Dimension {
    width: number;
    height: number;
}

const DIMENSION_MAP_MOBILE: Record<ItemType, Dimension> = {
    article: {
        width: 414,
        height: 336,
    },
    project: {
        width: 414,
        height: 378,
    },
};

const DIMENSION_MAP_DESKTOP: Record<ItemType, Dimension> = {
    article: BANNER_DIMENSIONS,
    project: {
        width: FULL_COLUMN_WIDTH_FULL_SCREEN,
        height: BANNER_HEIGHT_DESKTOP_FULL_SCREEN,
    },
};

/**
 * The revamped project hero is taller than the legacy banner: ProjectHeroSection
 * overrides DetailHero's geometry to 1200/868 on desktop and 414/520 on mobile.
 *
 * These dimensions drive the editor's frame *and* the crop box handed to the
 * image editor, so the page builder has to use the same ratio the live page
 * does — otherwise an author crops to the wide legacy box and the hero silently
 * chops the top and bottom back off.
 */
const PROJECT_REVAMP_DESKTOP: Dimension = {
    width: FULL_COLUMN_WIDTH_FULL_SCREEN,
    height: Math.round((FULL_COLUMN_WIDTH_FULL_SCREEN * 868) / 1200),
};

const PROJECT_REVAMP_MOBILE: Dimension = { width: 414, height: 520 };

/**
 * The article revamp's full-bleed hero, as `RevampHeroImage` draws it — taken
 * from the same constants the hero is drawn with rather than restated, because
 * the phone crop has already been retuned once.
 *
 * These dimensions drive the page builder's banner frame **and** the crop box
 * handed to the image editor. Off by themselves they would only make the
 * builder's masthead the wrong height; handed to the editor they decide the
 * frame the author crops inside, so leaving them on the legacy box has the
 * author crop wide and the reader's hero silently chop the rest off.
 */
const ARTICLE_REVAMP_DESKTOP: Dimension = {
    width: BANNER_DIMENSIONS.width,
    height: Math.round(BANNER_DIMENSIONS.width / REVAMP_HERO_DESKTOP_RATIO),
};

const ARTICLE_REVAMP_MOBILE: Dimension = {
    width: DIMENSION_MAP_MOBILE.article.width,
    height: Math.round(
        DIMENSION_MAP_MOBILE.article.width / REVAMP_HERO_MOBILE_RATIO,
    ),
};

/**
 * The two revamps each retune one item type's hero, and each is behind its own
 * flag, so both options are honoured and neither can answer for the other:
 * `projectRevamp` only reshapes a project, `articleRevamp` only an article.
 */
export const getBannerDimensions = (
    itemType: ItemType,
    {
        projectRevamp = false,
        articleRevamp = false,
    }: { projectRevamp?: boolean; articleRevamp?: boolean } = {},
) => {
    const useProjectRevamp = projectRevamp && itemType === 'project';
    const useArticleRevamp = articleRevamp && itemType === 'article';
    const mobileDimension = useProjectRevamp
        ? PROJECT_REVAMP_MOBILE
        : useArticleRevamp
          ? ARTICLE_REVAMP_MOBILE
          : DIMENSION_MAP_MOBILE[itemType];
    const desktopDimension = useProjectRevamp
        ? PROJECT_REVAMP_DESKTOP
        : useArticleRevamp
          ? ARTICLE_REVAMP_DESKTOP
          : DIMENSION_MAP_DESKTOP[itemType];
    return {
        ...{ ...BANNER_DIMENSIONS, ...desktopDimension },
        mobileWidth: mobileDimension.width,
        mobileHeight: mobileDimension.height,
    };
};
